import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { pipelineAPI } from '../api/client';
import AppIcon, { IconName } from '../components/AppIcon';
import { ALERT_TONES, CHART_GREEN } from '../utils/greenPalette';

const COMPONENT_CONFIG: Record<string, { icon: IconName; label: string }> = {
  orchestrator: { icon: 'cpu', label: 'Orchestrator' },
  ml_model: { icon: 'brain', label: 'ML Model' },
  active_session: { icon: 'activity', label: 'Session' },
  site_blocker: { icon: 'lock', label: 'Site Blocker' }
};

const STATUS_COLORS: Record<string, string> = {
  healthy: 'bg-green-100  text-green-700  border-green-200',
  active: 'bg-green-100  text-green-700  border-green-200',
  blocking: 'bg-red-100    text-red-700    border-red-200',
  down: 'bg-red-100    text-red-700    border-red-200',
  not_trained: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  idle: 'bg-gray-100   text-gray-600   border-gray-200',
  degraded: 'bg-orange-100 text-orange-700 border-orange-200'
};

const getAgentStateLabel = (agentState: unknown): string => {
  if (typeof agentState === 'string' && agentState.trim()) {
    return agentState;
  }

  if (agentState && typeof agentState === 'object') {
    const status = (agentState as { status?: unknown }).status;
    if (typeof status === 'string' && status.trim()) {
      return status;
    }
  }

  return 'unknown';
};

function AgentDashboard() {
  const [health, setHealth] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  useEffect(() => {
    loadAll();
    const interval = setInterval(() => {
      loadAll();
      setLastRefresh(new Date());
    }, 30_000);
    return () => clearInterval(interval);
  }, []);

  const loadAll = async () => {
    try {
      const [healthRes, summaryRes] = await Promise.allSettled([
        pipelineAPI.getHealth(),
        pipelineAPI.getSummary()
      ]);

      let failedCalls = 0;

      if (healthRes.status === 'fulfilled') {
        setHealth(healthRes.value.data);
      } else {
        failedCalls += 1;
        console.warn('Health fetch failed:', healthRes.reason);
      }

      if (summaryRes.status === 'fulfilled') {
        setSummary(summaryRes.value.data);
      } else {
        failedCalls += 1;
        console.warn('Summary fetch failed:', summaryRes.reason);
      }

      if (failedCalls === 0) {
        setLoadError(null);
      } else {
        setLoadError('Some dashboard data could not be refreshed. Showing last known state.');
      }
    } catch (error) {
      console.error('Dashboard load error:', error);
      setLoadError('Unable to load latest dashboard data. Showing last known state.');
    } finally {
      setLoading(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      await pipelineAPI.runNow();
      await loadAll();
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500 text-xl animate-pulse">
            Loading agent dashboard...
          </p>
        </div>
      </div>
    );
  }

  // Format risk timeline for chart
  const riskTimeline = (Array.isArray(summary?.risk_timeline) ? summary.risk_timeline : []).map((r: any) => ({
    time: new Date(r.assessed_at).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    }),
    risk: Math.round((Number(r.risk_score) || 0) * 100),
    score: r.risk_score
  }));

  const components = health && typeof health.components === 'object' && health.components
    ? health.components
    : {};
  const agentStateLabel = getAgentStateLabel(health?.agent_state);

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900"> Agent Dashboard</h1>
            <p className="text-gray-500 text-sm mt-1">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </p>
          </div>
          <button
            onClick={handleRunNow}
            disabled={running}
            className="px-5 py-2.5 bg-gray-900 text-white rounded-xl
              font-semibold hover:bg-gray-800 disabled:bg-gray-400
              transition text-sm"
          >
            {running ? ' Running...' : ' Run Pipeline Now'}
          </button>
        </div>

        {loadError && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 mb-6 text-amber-800 text-sm">
            {loadError}
          </div>
        )}

        {/* Overall status banner */}
        <div
          className={`rounded-xl border p-4 mb-8 flex items-center gap-4
          ${health?.overall_status === 'healthy'
            ? 'bg-green-50 border-green-200'
            : 'bg-orange-50 border-orange-200'
          }`}
        >
          <AppIcon
            name={health?.overall_status === 'healthy' ? 'shield-check' : 'shield-x'}
            className={health?.overall_status === 'healthy' ? 'text-green-700' : 'text-orange-700'}
            size={30}
          />
          <div>
            <p
              className={`font-bold text-lg ${
                health?.overall_status === 'healthy' ? 'text-green-800' : 'text-orange-800'
              }`}
            >
              System {health?.overall_status === 'healthy' ? 'Healthy' : 'Degraded'}
            </p>
            <p
              className={`text-sm ${
                health?.overall_status === 'healthy' ? 'text-green-600' : 'text-orange-600'
              }`}
            >
              Agent state: <strong>{agentStateLabel}</strong>
              {health?.last_risk_score != null &&
                `  Last risk: ${Math.round(health.last_risk_score * 100)}%`}
              {health?.last_cycle &&
                `  Last cycle: ${new Date(health.last_cycle).toLocaleTimeString()}`}
            </p>
          </div>
        </div>

        {/* Component health grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {Object.entries(components).map(([key, comp]: [string, any]) => {
            const cfg = COMPONENT_CONFIG[key] || { icon: 'event' as IconName, label: key };
            const status = typeof comp?.status === 'string' ? comp.status : 'idle';
            const description = typeof comp?.description === 'string' ? comp.description : 'No details available';
            const colors = STATUS_COLORS[status] || STATUS_COLORS.idle;

            return (
              <div key={key} className={`bg-white rounded-xl border p-4 ${colors}`}>
                <div className="flex items-center gap-2 mb-2">
                  <AppIcon name={cfg.icon} size={20} />
                  <p className="font-semibold text-sm">{cfg.label}</p>
                </div>
                <p className="text-xs capitalize font-medium">{status.replace(/_/g, ' ')}</p>
                <p className="text-xs opacity-75 mt-0.5 leading-tight">{description}</p>
              </div>
            );
          })}
        </div>

        {/* Today's stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon="chart"
            label="Cycles Today"
            value={summary?.total_cycles || 0}
            color="text-green-600"
          />
          <StatCard
            icon="warning"
            label="Interventions"
            value={health?.today?.interventions || 0}
            color="text-orange-600"
          />
          <StatCard
            icon="bolt"
            label="Actions Taken"
            value={health?.today?.autonomous_actions || 0}
            color="text-green-700"
          />
          <StatCard
            icon="trend-up"
            label="Peak Risk Today"
            value={`${Math.round((summary?.peak_risk_today || 0) * 100)}%`}
            color="text-red-600"
          />
        </div>

        {/* Risk timeline chart */}
        <div className="bg-white rounded-xl shadow p-6 mb-8">
          <h2 className="text-lg font-bold text-gray-900 mb-4"> Risk Timeline Today</h2>

          {riskTimeline.length === 0 ? (
            <div className="text-center py-12">
              <div className="flex justify-center mb-3">
                <AppIcon name="chart" className="text-gray-300" size={36} />
              </div>
              <p className="text-gray-500">No risk data yet. Start a session to begin monitoring.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={riskTimeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip
                  formatter={(value: any) => [`${value}%`, 'Risk Score']}
                  contentStyle={{
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    fontSize: '12px'
                  }}
                />
                <ReferenceLine
                  y={60}
                  stroke={ALERT_TONES.warning}
                  strokeDasharray="4 4"
                  label={{
                    value: 'At Risk',
                    position: 'right',
                    fontSize: 10,
                    fill: ALERT_TONES.warning
                  }}
                />
                <ReferenceLine
                  y={75}
                  stroke={ALERT_TONES.critical}
                  strokeDasharray="4 4"
                  label={{
                    value: 'Critical',
                    position: 'right',
                    fontSize: 10,
                    fill: ALERT_TONES.critical
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="risk"
                  stroke={CHART_GREEN.line}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 5, fill: CHART_GREEN.dot }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Bottom grid: Events + Interventions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Live event stream */}
          <div className="bg-white rounded-xl shadow p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4"> Live Event Stream</h2>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {(summary?.events_today || []).length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-8">No events yet today</p>
              ) : (
                (Array.isArray(summary?.events_today) ? summary.events_today : []).slice(0, 15).map((event: any, i: number) => (
                  <EventRow key={i} event={event} />
                ))
              )}
            </div>
          </div>

          {/* Today's interventions */}
          <div className="bg-white rounded-xl shadow p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4"> Today's Interventions</h2>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {(summary?.interventions || []).length === 0 ? (
                <div className="text-center py-8">
                  <div className="flex justify-center mb-2">
                    <AppIcon name="check-circle" className="text-gray-300" size={28} />
                  </div>
                  <p className="text-gray-400 text-sm">No interventions today. Great focus!</p>
                </div>
              ) : (
                (Array.isArray(summary?.interventions) ? summary.interventions : []).map((iv: any, i: number) => (
                  <InterventionRow key={i} intervention={iv} />
                ))
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  color
}: {
  icon: IconName;
  label: string;
  value: any;
  color: string;
}) {
  return (
    <div className="bg-white rounded-xl shadow p-5">
      <div className="flex items-center gap-2 mb-1">
        <AppIcon name={icon} className="text-gray-500" size={18} />
        <p className="text-sm text-gray-500">{label}</p>
      </div>
      <p className={`text-3xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

function EventRow({ event }: { event: any }) {
  const typeIcons: Record<string, IconName> = {
    pipeline_cycle: 'cycle',
    alert_warning: 'warning',
    alert_intervention: 'intervention',
    alert_recovery: 'recovery',
    monitoring_cycle: 'activity'
  };

  const eventType = typeof event?.event_type === 'string' ? event.event_type : 'unknown_event';
  const icon = typeIcons[eventType] || 'event';
  const createdAt = event?.created_at ? new Date(event.created_at) : null;

  return (
    <div className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
      <AppIcon name={icon} className="text-gray-400 mt-0.5" size={16} />
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center">
          <p className="text-xs font-medium text-gray-700 capitalize truncate">
            {eventType.replace(/_/g, ' ')}
          </p>
          <p className="text-xs text-gray-400 shrink-0 ml-2">
            {(createdAt && !Number.isNaN(createdAt.getTime())) ? createdAt.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            }) : '--:--'}
          </p>
        </div>
        {event.event_data?.risk_score != null && (
          <p className="text-xs text-gray-500">
            Risk: {Math.round(event.event_data.risk_score * 100)}%
            {event.state_after && `  ${event.state_after}`}
          </p>
        )}
      </div>
    </div>
  );
}

function InterventionRow({ intervention }: { intervention: any }) {
  const outcomeColors: Record<string, string> = {
    success: 'text-green-600',
    ignored: 'text-gray-400',
    partial: 'text-yellow-600',
    pending: 'text-green-600'
  };

  const interventionType = typeof intervention?.intervention_type === 'string'
    ? intervention.intervention_type
    : 'unknown';
  const outcome = typeof intervention?.outcome === 'string' ? intervention.outcome : 'pending';
  const createdAt = intervention?.created_at ? new Date(intervention.created_at) : null;

  return (
    <div className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
      <AppIcon name="warning" className="text-gray-400 mt-0.5" size={16} />
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center">
          <p className="text-xs font-medium text-gray-700 capitalize truncate">
            {interventionType.replace(/_/g, ' ')}
          </p>
          <p
            className={`text-xs font-semibold shrink-0 ml-2 capitalize
            ${outcomeColors[outcome] || 'text-gray-500'}`}
          >
            {outcome}
          </p>
        </div>
        <p className="text-xs text-gray-500">
          Risk: {Math.round((intervention.risk_score_at_trigger || 0) * 100)}%
          {'  '}
          {(createdAt && !Number.isNaN(createdAt.getTime())) ? createdAt.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          }) : '--:--'}
        </p>
      </div>
    </div>
  );
}

export default AgentDashboard;
